/* custom-brand.js — open Dawn's desktop header menus on hover.
   Mouse only: keyboard and touch keep the native <details> behaviour (click /
   Enter / Space). Escape closes an open menu and returns focus to its trigger.
   Loaded by snippets/custom-head.liquid; generic Dawn, nothing shop-specific. */
(() => {
  const CLOSE_DELAY_MS = 150; // lets the pointer cross the gap into the submenu
  const hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');

  const setOpen = (menu, open) => {
    const details = menu.querySelector('details');
    const summary = details && details.querySelector('summary');
    if (!details || !summary || details.open === open) return;
    details.open = open; // fires "toggle", so Dawn's HeaderMenu bookkeeping still runs
    summary.setAttribute('aria-expanded', String(open));
  };

  document.querySelectorAll('header-menu').forEach((menu) => {
    let closeTimer;

    menu.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse' || !hoverCapable.matches) return;
      clearTimeout(closeTimer);
      setOpen(menu, true);
    });

    menu.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse' || !hoverCapable.matches) return;
      closeTimer = setTimeout(() => setOpen(menu, false), CLOSE_DELAY_MS);
    });

    menu.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const details = menu.querySelector('details');
      if (!details || !details.open) return;
      event.preventDefault();
      setOpen(menu, false);
      details.querySelector('summary').focus();
    });
  });
})();

/* Desktop menu overlay: reserve exactly the room the logo and the icon group
   need (custom-brand.css uses --brand-nav-logo-space / --brand-nav-icons-space
   as minimums). Measured, so a wide text logo never sits under the menu.
   A shop can still force a value in custom-design.css: the measured one only
   applies when it is larger. */
(() => {
  const header = document.querySelector('.header-wrapper');
  if (!header) return;
  const measure = () => {
    if (window.innerWidth < 990) return;
    const logo = header.querySelector('.header__heading, .header__heading-link');
    const icons = header.querySelector('.header__icons');
    const headerRect = header.querySelector('.header')?.getBoundingClientRect();
    if (!logo || !icons || !headerRect) return;
    const rtl = document.documentElement.dir === 'rtl';
    const l = logo.getBoundingClientRect(), i = icons.getBoundingClientRect();
    // distance from the header's inline-start edge to the logo's inline-end edge (+ gap)
    const centred = /header--(top|middle)-center/.test(header.querySelector('.header')?.className || '');
    const logoSpace = centred ? 0 : (rtl ? headerRect.right - l.left : l.right - headerRect.left) + 24;
    const iconsSpace = (rtl ? i.right - headerRect.left : headerRect.right - i.left) + 24;
    header.style.setProperty('--brand-nav-logo-space', `max(${Math.ceil(logoSpace)}px, var(--brand-nav-logo-space-min, 0px))`);
    header.style.setProperty('--brand-nav-icons-space', `max(${Math.ceil(iconsSpace)}px, var(--brand-nav-icons-space-min, 0px))`);
  };
  measure();
  window.addEventListener('resize', measure);
  window.addEventListener('load', measure);
  document.fonts?.ready.then(measure);
})();

/* a11y: Dawn's quantity <input> ends up with an empty accessible name (its <label> text is aria-hidden and the
   aria-labelledby is self-referential), so give every quantity input an explicit aria-label = the visible label text. */
(() => {
  const fix = () => document.querySelectorAll('input.quantity__input:not([aria-label])').forEach((i) => {
    const lbl = i.labels && i.labels[0];
    const txt = (lbl && (lbl.querySelector('span:not(.quantity__rules-cart)')?.textContent || lbl.textContent).trim().split('\n')[0].trim()) || (document.documentElement.lang === 'he' ? 'כמות' : 'Quantity');
    i.setAttribute('aria-label', txt);
  });
  fix(); document.addEventListener('shopify:section:load', fix); let raf = 0; new MutationObserver(() => { if (raf) return; raf = requestAnimationFrame(() => { raf = 0; fix(); }); }).observe(document.body, { childList: true, subtree: true });
})();

/* Hover quick-add (opt-in per shop: custom-design.css sets --brand-quick-add-hover: 1). Dawn renders the quick-add
   button under the card; here it is moved into the media box so CSS can float it over the photo. Touch devices and
   keyboard focus keep it visible (see custom-design.css). */
(() => {
  if (getComputedStyle(document.documentElement).getPropertyValue('--brand-quick-add-hover').trim() !== '1') return;
  const move = () => document.querySelectorAll('.card-wrapper .card__content > .quick-add').forEach((q) => {
    const inner = q.closest('.card')?.querySelector('.card__inner'); if (inner && !inner.contains(q)) { q.classList.add('quick-add--hover'); inner.appendChild(q); }
  });
  move(); document.addEventListener('shopify:section:load', move); let raf2 = 0; new MutationObserver(() => { if (raf2) return; raf2 = requestAnimationFrame(() => { raf2 = 0; move(); }); }).observe(document.body, { childList: true, subtree: true });
})();

/* Sort labels: Shopify outputs collection.sort_options names in English even on Hebrew shops; map them here
   (values are Shopify's sort keys, so any theme benefits). */
(() => {
  const he = { manual: 'מומלצים', 'best-selling': 'הנמכרים ביותר', 'title-ascending': 'שם: א–ת', 'title-descending': 'שם: ת–א', 'price-ascending': 'מחיר: מהנמוך לגבוה', 'price-descending': 'מחיר: מהגבוה לנמוך', 'created-ascending': 'תאריך: מהישן לחדש', 'created-descending': 'תאריך: מהחדש לישן', relevance: 'הרלוונטיים ביותר', 'best-match': 'הרלוונטיים ביותר', 'most-relevant': 'הרלוונטיים ביותר' };
  if (document.documentElement.lang !== 'he') return;
  const fix = () => document.querySelectorAll('select[name="sort_by"] option').forEach((o) => { if (he[o.value] && o.textContent !== he[o.value]) o.textContent = he[o.value]; });
  fix(); document.addEventListener('shopify:section:load', fix); let raf = 0; new MutationObserver(() => { if (raf) return; raf = requestAnimationFrame(() => { raf = 0; fix(); }); }).observe(document.body, { childList: true, subtree: true });
})();


/* Dawn's product media slider scrolls horizontally on phones but is not reachable by keyboard
   (axe: scrollable-region-focusable). Adding tabindex keeps the gallery usable without a mouse. */
(() => {
  const fix = () => document.querySelectorAll('.product__media-list, .slider--mobile').forEach((el) => {
    if (!el.hasAttribute('tabindex') && el.scrollWidth > el.clientWidth) el.setAttribute('tabindex', '0');
  });
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fix) : fix();
  window.addEventListener('resize', fix, { passive: true });
})();


/* ---- social links open in a new tab ------------------------------------------------
   Dawn renders the footer/utility-bar social icons from `snippets/social-icons.liquid`
   with no target, so a click walks the shopper off the shop. Done here, in an added
   file, so a Dawn update cannot undo it. IS 5568 / WCAG 3.2.5: a link that opens a new
   window has to say so, hence the visually-hidden note taken from Dawn's own
   `general.accessibility.link_messages.new_window` string. */
(() => {
  const EXTERNAL = /facebook|instagram|tiktok|youtube|twitter|x\.com|whatsapp|wa\.me|linkedin|pinterest|snapchat|vimeo|tumblr/i;
  const note = window.DIGI_NEW_WINDOW || '';
  const mark = () => {
    document.querySelectorAll('a.list-social__link[href], .social-icons a[href]').forEach((a) => {
      if (a.dataset.digiBlank || !EXTERNAL.test(a.href)) return;
      a.dataset.digiBlank = '1';
      a.target = '_blank';
      a.rel = [...new Set((a.rel || '').split(/\s+/).concat(['noopener', 'noreferrer'])).values()].filter(Boolean).join(' ');
      if (note && !a.textContent.includes(note)) {
        const s = document.createElement('span');
        s.className = 'visually-hidden';
        s.textContent = ' ' + note;
        a.appendChild(s);
      }
    });
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', mark) : mark();
  document.addEventListener('shopify:section:load', mark);
})();
